import { Maybe } from '#/utils/types';
import ToastTitle from '#/components/common/toast/Title';
import Error from '#/components/common/toast/Error';
import Info from '#/components/common/toast/Info';
import Success from '#/components/common/toast/Success';
import Action from '#/components/common/toast/Action';

import WithdrawIcon from '#/assets/WithdrawIcon';

import * as PortfolioNavigationFn from '#/features/navigation/portfolio-functions';

import { Link } from './atoms';

interface WithdrawAmountProps {
  readonly amount: string;
}

interface CustomMessageProps {
  readonly message: Maybe<string>;
}

/**
 * Toast to display whenever the UI detects
 * an unknown error in the withdraw process.
 */
export function WithdrawUnexpectedError() {
  return (
    <Error>
      <ToastTitle>Withdraw to wallet failed</ToastTitle>
      Unexpected error. Try again.
    </Error>
  );
}

/**
 * Toast to display when the UI sends the initiate
 * withdraw request to StarkNet successfully.
 */
export function InitiateWithdrawRequestSent(props: WithdrawAmountProps) {
  const icon = <WithdrawIcon size={19} />;
  return (
    <Info icon={icon}>
      <ToastTitle>Withdrawal to bridge initiated</ToastTitle>
      {props.amount}
    </Info>
  );
}

/**
 * Toast to display when the UI tries to send the initiate
 * withdraw request to StarkNet but fails in doing so. Also
 * used when the API reports that the withdraw transfer has
 * failed due to L2 transaction being rejected.
 */
export function InitiateWithdrawRequestFailed() {
  return (
    <Error>
      <ToastTitle>Withdraw to wallet failed</ToastTitle>
      Withdrawal error. Try again.
    </Error>
  );
}

/**
 * Toast to display when the API reported that the withdraw
 * transfer was identified and is created in the PENDING state.
 */
export function InitiateWithdrawRequestInProgress(props: WithdrawAmountProps) {
  const icon = <WithdrawIcon size={19} />;
  return (
    <Info icon={icon}>
      <ToastTitle>Withdrawal to bridge in progress</ToastTitle>
      {props.amount}
    </Info>
  );
}

/**
 * Toast to display when the UI successfully sends the
 * Ethereum transaction to collect funds from the L1 Bridge.
 */
export function WithdrawToWalletRequestSent(props: WithdrawAmountProps) {
  const icon = <WithdrawIcon size={19} />;
  return (
    <Info icon={icon}>
      <ToastTitle>Withdrawal to wallet initiated</ToastTitle>
      {props.amount}
    </Info>
  );
}

/**
 * Toast to display when the UI tries to send the Ethereum
 * transaction to collect funds from the L1 Bridge but fails.
 */
export function WithdrawToWalletRequestFailed(props: CustomMessageProps) {
  return (
    <Error>
      <ToastTitle>Withdrawal to bridge failed</ToastTitle>
      {props.message ?? 'Transaction error. Try again.'}
    </Error>
  );
}

/**
 * Toast to display when the withdrawal is ready
 * to be collected on L1.
 */
export function WithdrawRequestReady(props: WithdrawAmountProps) {
  const icon = <WithdrawIcon size={19} />;
  return (
    <Action icon={icon}>
      <ToastTitle>Withdrawal to wallet available</ToastTitle>
      {props.amount}
      <Link to={PortfolioNavigationFn.linkToActiveTab('deposit-withdraw')}>
        Go to Withdrawals
      </Link>
    </Action>
  );
}

/**
 * Toast to display when the withdrawal is successful on L1
 */
export function WithdrawSuccessful(props: WithdrawAmountProps) {
  return (
    <Success>
      <ToastTitle>Withdrawal to wallet successful</ToastTitle>
      {props.amount}
    </Success>
  );
}
